import React, { useCallback, useMemo, useState } from 'react';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';

import { fg } from '@confluence/feature-gating';

import {
	createAttachmentActionExtensionData,
	type AttachmentActionContext,
	type ForgeUIAttachmentActionExtensionType,
} from '@confluence/forge-ui/entry-points/attachmentAction.ts';
import { AttachmentActionForgeApp } from '@confluence/forge-ui/entry-points/apps.ts';
import { useEligibleAttachmentActions } from '@confluence/forge-ui/entry-points/useEligibleAttachmentActions.ts';
import { extensionTitle } from '@confluence/forge-ui/entry-points/extensionTitle.ts';

const styles = cssMap({
	hiddenExtensionHost: {
		display: 'none',
	},
});

export type AttachmentMacroActionContext = Omit<
	AttachmentActionContext,
	'attachmentId' | 'surface'
>;

const AttachmentActionButton = ({
	extension,
	onSelect,
}: {
	extension: ForgeUIAttachmentActionExtensionType;
	onSelect: (extension: ForgeUIAttachmentActionExtensionType) => void;
}): React.JSX.Element => {
	const handleClick = useCallback(() => {
		onSelect(extension);
	}, [extension, onSelect]);

	return (
		<Button
			appearance="default"
			spacing="compact"
			onClick={handleClick}
			testId={`forge-attachment-action-${extension.id}`}
		>
			{extensionTitle(extension)}
		</Button>
	);
};

type Props = {
	attachmentId: string;
	context: AttachmentMacroActionContext;
	extensions: ReadonlyArray<ForgeUIAttachmentActionExtensionType>;
};

export const AttachmentMacroForgeActions = (props: Props): React.JSX.Element => {
	const { attachmentId, context, extensions: legacyExtensions } = props;
	const extensions = useEligibleAttachmentActions(attachmentId, legacyExtensions);
	// Reset an open app when the attachment context or its eligible actions change.
	const contextKey = useMemo(
		() =>
			JSON.stringify([
				attachmentId,
				context.content,
				context.space,
				extensions.map(({ id }) => id),
			]),
		[attachmentId, context.content, context.space, extensions],
	);
	return <EligibleActions key={contextKey} {...props} extensions={extensions} />;
};

const EligibleActions = ({
	attachmentId,
	context,
	extensions,
}: Props): React.JSX.Element | null => {
	const [activeExtension, setActiveExtension] =
		useState<ForgeUIAttachmentActionExtensionType | null>(null);
	const handleSelectExtension = useCallback((extension: ForgeUIAttachmentActionExtensionType) => {
		setActiveExtension(extension);
	}, []);
	const handleTearDown = useCallback(() => {
		setActiveExtension(null);
	}, []);

	const extensionData = useMemo(() => {
		const data = createAttachmentActionExtensionData({
			attachmentId,
			content: context.content,
			space: context.space,
			surface: 'attachmentMacro',
		});
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates -- independent rollback for the context field
		if (fg('forge_attachment_action_context_title')) {
			if (typeof activeExtension?.properties?.title === 'string') {
				return { ...data, title: activeExtension.properties.title };
			}
		}
		return data;
	}, [attachmentId, context.content, context.space, activeExtension]);

	if (!extensions.length) {
		return null;
	}

	return (
		<>
			{extensions.map((extension) => (
				<AttachmentActionButton
					key={extension.id}
					extension={extension}
					onSelect={handleSelectExtension}
				/>
			))}
			{activeExtension && (
				<Box xcss={styles.hiddenExtensionHost}>
					<AttachmentActionForgeApp
						key={activeExtension.id}
						app={activeExtension}
						extensionData={extensionData}
						onTearDown={handleTearDown}
					/>
				</Box>
			)}
		</>
	);
};
